import IconWithFallback from "Global/IconWithFallback";
import { MOBILE_MENU, MOBILE_SEARCH } from "common/constants";
import WatchlistPrice from "features/watchlist/common/WatchlistPrice";
import React from "react";
import styled, { useTheme } from "styled-components";
import Card from "common/Boxes/Card";
import Title from "common/Typography/Title";
import AntCloseOutlined from "@ant-design/icons/CloseOutlined";
import AntHolderOutlined from "@ant-design/icons/HolderOutlined";
import { Market } from "types";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import LiveSmallChart from "common/LiveComponents/LiveSmallChart";
import { CoinName, ExchangeName } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import NewLookSymbolCard from "features/watchlist/WatchlistCards/NewLookSymbolCard";

interface SymbolCardProps {
  watchlistMarket: Market;
  onRemove: (symbol: CoinName, exchange: ExchangeName) => void;
  ignoreOnClick?: boolean;
}

const SymbolCard = ({
  watchlistMarket,
  onRemove,
  ignoreOnClick,
}: SymbolCardProps) => {
  const isTablet = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { historyPush } = useHistoryWithQuery();
  const appTheme = useTheme();

  const openCoinExtraDetails = () => {
    if (!ignoreOnClick) {
      historyPush(
        getCoinExtraDetailsRoute(
          location.pathname || "",
          watchlistMarket.symbol
        )
      );
    }
  };

  const onRemoveSymbol = () => {
    onRemove(watchlistMarket.symbol, watchlistMarket.exchange);
  };

  if (appTheme.isNewLook) {
    return (
      <NewLookSymbolCard
        watchlistMarket={watchlistMarket}
        onOpen={openCoinExtraDetails}
        onRemove={onRemoveSymbol}
      />
    );
  }

  return (
    <StyledCard onClick={openCoinExtraDetails} onLongPress={onRemoveSymbol}>
      <Wrapper>
        <div>
          <Flex $justifyContent="flex-start" $alignItems="flex-start">
            <IconWithFallback
              coinOrExchangeName={watchlistMarket.symbol}
              iconStyle={{
                marginRight: isMobile ? 6 : 12,
                top: 2,
                position: "relative",
              }}
              iconType={ICON_TYPES.COIN}
              iconSize={isMobile ? "l" : "xxl"}
            />
            <SymbolExchangeWrapper>
              <SymbolTitle level={isMobile ? 4 : 2}>
                {watchlistMarket.symbol}
              </SymbolTitle>
              <Exchange type="secondary" $ellipsis={true}>
                {watchlistMarket.exchange}
              </Exchange>
            </SymbolExchangeWrapper>
          </Flex>
          <CloseOutlined
            onClick={(event) => {
              onRemoveSymbol();
              event.stopPropagation();
            }}
          />
        </div>
        <GraphWrapper>
          <LiveSmallChart
            symbol={watchlistMarket.symbol}
            exchange={watchlistMarket.exchange}
            initialGraphData={watchlistMarket.graph_data}
            isPrice={true}
            yValueIndex={1}
            chartHeight={isMobile ? 32 : undefined}
          />
        </GraphWrapper>
      </Wrapper>
      <PriceWrapper>
        <WatchlistPrice
          market={watchlistMarket}
          fontSize={
            isMobile
              ? appTheme.typography.fontSizes.M
              : appTheme.typography.fontSizes.L
          }
          textColor={appTheme.colors.primaryText}
          showNoneDisplayCurrencyIcons={true}
        />
        {!isTablet && (
          <HolderOutlined onChange={(event) => event.stopPropagation()} />
        )}
      </PriceWrapper>
    </StyledCard>
  );
};

export default SymbolCard;

const GRAPH_WIDTH = "45%";

const StyledCard = styled(Card)`
  height: 164px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 16px 12px;
  cursor: pointer;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px 12px 10px;
    height: 147px;
  }
`;
const Wrapper = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;
const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 10px;
  border-top: ${(p) => p.theme.borders.medium};
  position: relative;
`;
const SymbolExchangeWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;
const Exchange = styled(Text)`
  max-width: calc(100% - ${GRAPH_WIDTH} - 40px);
  @media (max-width: ${MOBILE_SEARCH}) {
    max-width: 100%;
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
`;
const SymbolTitle = styled(Title)`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const CloseOutlined = styled(AntCloseOutlined)`
  position: absolute;
  right: 0;
  top: 0;
  padding: 16px;
  color: ${(p) => p.theme.colors.textColor};
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px;
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
`;
const HolderOutlined = styled(AntHolderOutlined)`
  position: absolute;
  bottom: 5px;
  right: 0;
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  color: ${(p) => p.theme.colors.textColor};
  cursor: move;
`;
const GraphWrapper = styled.div`
  width: ${GRAPH_WIDTH};
  position: absolute;
  top: 48px;
  right: 16px;
  @media (max-width: ${MOBILE_SEARCH}) {
    position: unset;
    width: 100%;
  }
`;
